import React, { useEffect, Fragment } from 'react';
import { useScreen } from 'common/hooks/useScreen';
import { RedTheme } from 'common/global';
import { fontWeightStyles } from 'TAP/styles/genericStyles/fontWeights';
import { Box, Stack, Typography, Grid } from '@mui/material';
import { ToolTipWrapper, UserProfileImage } from 'common/components';
import { TapGlobalMessages } from 'TAP/global';
import { FallbackAvatar } from 'TAP/components/UserGroupProfile/FallbackAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { UGP_MY_PROFILE_LABEL_KEYS, NUMBER_ARRAY } from 'common/global/constants';
import { MyProfileJobRoleDetailsAction } from 'TAP/redux/actions';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { EllipsisTypography } from 'TAP/components/UserGroupProfile/EllipsisTypography';

const containerPadding = {
  mobile: '16px',
  tablet: '0 24px 24px 24px',
  desktop: '0 72px 0 72px',
};

export const MyProfile = ({ loggedInUserDetails }) => {
  const screen = useScreen();
  const redtheme = RedTheme;
  let userInitials = loggedInUserDetails?.full_name
    ?.split(',')
    .map((name) => name.trim()[NUMBER_ARRAY.zero].toUpperCase())
    .join('');

  const dispatch = useDispatch();
  const myProfileJobRoleDetailsData = useSelector(state => state?.MyProfileJobRoleDetailsRd?.MyProfileJobRoleDetailsRd?.data);

  const infoHeaderStyle = {
    fontSize: screen.resolveStyles({
      mobile: '10px',
      tablet: '16px',
      desktop: '16px',
    }),
    lineHeight: '16px',
    color: redtheme.palette.text.lightCaption,
    fontWeight: screen.resolveStyles({
      mobile: fontWeightStyles.fw5.fontWeight,
      tablet: fontWeightStyles.fw6.fontWeight,
      desktop: fontWeightStyles.fw7.fontWeight,
    }),
    fontFamily: '"JohnsonText-Regular"',
  };

  const infoCaptionStyle = {
    fontSize: screen.resolveStyles({
      mobile: '10px',
      tablet: '14px',
      desktop: '14px',
    }),
    lineHeight: '16px',
    color: redtheme.palette.text.lightCaption,
    fontWeight: screen.resolveStyles({
      mobile: fontWeightStyles.fw5.fontWeight,
      tablet: fontWeightStyles.fw6.fontWeight,
      desktop: fontWeightStyles.fw7.fontWeight,
    }),
    fontFamily: '"JohnsonText-Regular"',
    width: '180px'
  };

  const headingTextStyle = {
    width: '100%',
    fontSize: screen.resolveStyles({
      mobile: '18px',
      tablet: '20px',
      desktop: '25px',
    }),
    lineHeight: '32px',
    fontFamily: '"JohnsonDisplay-Bold"',
    fontWeight: fontWeightStyles.fw7.fontWeight,
    padding: 0,
    marginBottom: '24px',
  };

  const myProfilePageContainerStyle = {
    background: redtheme.palette.background.default,
    display: 'flex',
    width: '100%',
    padding: screen.resolveStyles(containerPadding),
    flexDirection: 'column',
  };

  const basicInfoContainerStyle = {
    width: '100%',
    border: `1px solid ${redtheme.palette.border}`,
    padding: '20px 20px',
    margin: '24px 0px',
    borderRadius: '8px',
  };

  const userNameTypographyStyle = {
    fontFamily: '"JohnsonText-Regular"',
    fontSize: screen.resolveStyles({
      mobile: '24px',
      tablet: '32px',
      desktop: '32px',
    }),
    color: redtheme.palette.text.base,
  };

  const basicProfileTextStyle = {
    fontFamily: '"JohnsonText-Regular"',
    fontSize: screen.resolveStyles({
      mobile: '10px',
      tablet: '14px',
      desktop: '14px',
    }),
    color: redtheme.palette.text.primary,
  };

  const basicInfoInnerContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '20px',
  };

  const jobRoleDetailsValuesStyle = {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }

  const renderJobRoleDetailsHeaderTooltip = (Header, title, param) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Typography sx={infoCaptionStyle}>{Header}</Typography>
        <ToolTipWrapper
          title={title}
          placement='top-start'
          isOnlyText={true}
          isCustomHtmlTooltip={true}
          tooltipCss={{
            padding: '4px 8px',
          }}
        >
          <HelpOutlineIcon
            fontSize='medium'
            sx={{
              color: `${redtheme.palette.common.primaryMainBlue}`,
              height: '15px',
              width: '15px',
              marginLeft: !screen.isMobile && `${param}`,
            }}
          />
        </ToolTipWrapper>
      </Box>

    );
  }

  useEffect(() => {
    if (loggedInUserDetails) {
      const wwid = loggedInUserDetails?.user_id;
      const payload = {
        wwid: wwid
      }
      dispatch(MyProfileJobRoleDetailsAction(payload))
    }
  }, [loggedInUserDetails])


  return (
    <Box sx={myProfilePageContainerStyle}>
      <Box sx={basicInfoContainerStyle}>
        <Box sx={basicInfoInnerContainerStyle}>
          {loggedInUserDetails?.loggedInUserType === 'delegate' ? (
            <FallbackAvatar height={'120px'} userInitials={userInitials} />
          ) : (
            <UserProfileImage height={'120px'}></UserProfileImage>
          )}
          <Stack>
            <Typography data-testid={'basic-info-label'} sx={basicProfileTextStyle}>
              {TapGlobalMessages.userGroupProfile.myProfile.basicInfoLabel}
            </Typography>
            <Typography data-testid={'user-name'} sx={userNameTypographyStyle}>
              {loggedInUserDetails?.full_name}
            </Typography>
            <Typography
              sx={infoHeaderStyle}
              data-testid={'user-id-and-email'}
            >{`WWID: ${loggedInUserDetails?.user_id}`}</Typography>
          </Stack>
        </Box>
        <Grid container spacing={2}>
          <Grid item key={UGP_MY_PROFILE_LABEL_KEYS.jobTittle} xs={12} sm={4} md={3}>
            <Stack>
              <Typography data-testid={'job-title-label'} sx={infoCaptionStyle}>
                {TapGlobalMessages.userGroupProfile.myProfile.jobTittleLabel}
              </Typography>
              <Typography data-testid={'job-title-value'}>
                {loggedInUserDetails?.job_code || '-'}
              </Typography>
            </Stack>
          </Grid>
          <Grid item key={UGP_MY_PROFILE_LABEL_KEYS.workerType} xs={12} sm={4} md={3}>
            <Stack>
              <Typography data-testid={'worker-type-label'} sx={infoCaptionStyle}>
                {TapGlobalMessages.userGroupProfile.myProfile.workerTypeLabel}
              </Typography>
              <Typography data-testid={'worker-type-value'}>
                {loggedInUserDetails?.worker_type || '-'}
              </Typography>
            </Stack>
          </Grid>
          <Grid item key={UGP_MY_PROFILE_LABEL_KEYS.partnerCompany} xs={12} sm={4} md={2}>
            <Stack>
              <Typography data-testid={'partner-company-label'} sx={infoCaptionStyle}>
                {TapGlobalMessages.userGroupProfile.myProfile.partnerCompanyLabel}
              </Typography>
              <Typography data-testid={'partner-company-value'}>
                {loggedInUserDetails?.partner_company || '-'}
              </Typography>
            </Stack>
          </Grid>
          <Grid item key={UGP_MY_PROFILE_LABEL_KEYS.country} xs={12} sm={4} md={2}>
            <Stack>
              <Typography data-testid={'country-label'} sx={infoCaptionStyle}>
                {TapGlobalMessages.userGroupProfile.myProfile.countryLabel}
              </Typography>
              <Typography data-testid={'country-value'}>{`${
                loggedInUserDetails?.country_name || '-'
              } (${loggedInUserDetails?.region || '-'})`}</Typography>
            </Stack>
          </Grid>
          <Grid item key={UGP_MY_PROFILE_LABEL_KEYS.onLeave} xs={12} sm={4} md={2}>
            <Stack>
              <Typography data-testid={'on-leave-label'} sx={infoCaptionStyle}>
                {TapGlobalMessages.userGroupProfile.myProfile.onLeaveLabel}
              </Typography>
              <Typography data-testid={'on-leave-value'}>
                {loggedInUserDetails?.leave_of_absence === 'Y' ? 'Yes' : 'No'}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ padding: '24px 10px 50px 10px' }}>
        {myProfileJobRoleDetailsData?.length > NUMBER_ARRAY.zero ? (
          <Typography variant='h3' sx={headingTextStyle}>
            {TapGlobalMessages?.userGroupProfile?.myProfile?.jobRoleDetailsLabel}
          </Typography>
        ) : (
          <Box></Box>
        )}
        {/* This Section needs to change */}
        {myProfileJobRoleDetailsData?.length > NUMBER_ARRAY.zero ? (
          <Grid container spacing={2}>
            {/* Render the labels only once */}
            {/* Loop through each obj to display the values */}
            {myProfileJobRoleDetailsData.map((obj, index) => (
              <Fragment key={`my_team_profile${index}`}>
                <Grid item  xs={12} sm={4} md={3}>
                  <Stack>
                    {renderJobRoleDetailsHeaderTooltip(
                      'Organization:',
                      TapGlobalMessages?.userGroupProfile?.myProfile?.toolTip?.organization,
                      '-95px',
                    )}
                    <EllipsisTypography
                      title={obj?.organization}
                      TypographyElement={
                        <Typography sx={jobRoleDetailsValuesStyle}>
                          {obj?.organization || 'Not Applicable'}
                        </Typography>
                      }
                      maxWidth={NUMBER_ARRAY.twoHundred}
                    />
                  </Stack>
                </Grid>
                <Grid item  xs={12} sm={4} md={3}>
                  <Stack>
                    {renderJobRoleDetailsHeaderTooltip(
                      'Function:',
                      TapGlobalMessages?.userGroupProfile?.myProfile?.toolTip?.function,
                      '-120px',
                    )}
                    <EllipsisTypography
                      title={obj?.function}
                      TypographyElement={
                        <Typography sx={jobRoleDetailsValuesStyle}>
                          {obj?.function || 'Not Applicable'}
                        </Typography>
                      }
                      maxWidth={NUMBER_ARRAY.twoHundred}
                    />
                  </Stack>
                </Grid>
                <Grid item  xs={12} sm={4} md={3}>
                  <Stack>
                    {renderJobRoleDetailsHeaderTooltip(
                      'Sub function:',
                      TapGlobalMessages?.userGroupProfile?.myProfile?.toolTip?.subFunction,
                      '-95px',
                    )}
                    <EllipsisTypography
                      title={obj?.sub_function}
                      TypographyElement={
                        <Typography sx={jobRoleDetailsValuesStyle}>
                          {obj?.sub_function || 'Not Applicable'}
                        </Typography>
                      }
                      maxWidth={NUMBER_ARRAY.twoHundred}
                    />
                  </Stack>
                </Grid>
                <Grid item  xs={12} sm={4} md={3}>
                  <Stack>
                    {renderJobRoleDetailsHeaderTooltip(
                      'Specialization:',
                      TapGlobalMessages?.userGroupProfile?.myProfile?.toolTip?.specialization,
                      '-87px',
                    )}
                    <EllipsisTypography
                      title={obj?.specialization}
                      TypographyElement={
                        <Typography sx={jobRoleDetailsValuesStyle}>
                          {obj?.specialization || 'Not Applicable'}
                        </Typography>
                      }
                      maxWidth={NUMBER_ARRAY.twoHundred}
                    />
                  </Stack>
                </Grid>
                <Grid item  xs={12} sm={4} md={3}>
                  <Stack>
                    {renderJobRoleDetailsHeaderTooltip(
                      'Sub Specialization:',
                      TapGlobalMessages?.userGroupProfile?.myProfile?.toolTip?.subSpecialization,
                      '-60px',
                    )}
                    <EllipsisTypography
                      title={obj?.sub_specialization}
                      TypographyElement={
                        <Typography sx={jobRoleDetailsValuesStyle}>
                          {obj?.sub_specialization || 'Not Applicable'}
                        </Typography>
                      }
                      maxWidth={NUMBER_ARRAY.twoHundred}
                    />
                  </Stack>
                </Grid>
                <Grid item  xs={12} sm={4} md={3}>
                  <Stack>
                    <Typography sx={infoCaptionStyle}>Job Role:</Typography>
                    <EllipsisTypography
                      title={obj?.job_role}
                      TypographyElement={
                        <Typography sx={jobRoleDetailsValuesStyle}>
                          {obj?.job_role || 'Not Applicable'}
                        </Typography>
                      }
                      maxWidth={NUMBER_ARRAY.twoHundred}
                    />
                  </Stack>
                </Grid>
              </Fragment>
            ))}
          </Grid>
        ) : (
          <Box></Box>
        )}
      </Box>
    </Box>
  );
};
